import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  alpha,
  Grid,
  Fade,
  Zoom,
  Dialog,
  DialogContent,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SparklesIcon from "@mui/icons-material/AutoAwesome";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Verified from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import emailjs from "@emailjs/browser";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConsultationFormDialog = ({ open, onClose }) => {
  const colors = {
    charcoalGrey: "#232933ff",
    charcoalLight: "#b2b3b4ff",
    terracotta: "#e53e3e",
    terracottaDark: "#c53030",
    pureWhite: "#ffffff",
  };

  const fonts = {
    header: "'Playfair Display', serif",
    body: "'Montserrat', sans-serif",
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "Commercial Space Design",
    budget: "To be discussed",
    company: "",
    location: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const projectTypes = ["Commercial Space Design", "Residential Space Design"];

  const budgets = [
    "To be discussed",
    "Under ₹50,000",
    "₹50,000 - ₹1,00,000",
    "₹1,00,000 - ₹5,00,000",
    "₹5,00,000 - ₹10,00,000",
    "₹10,00,000 - ₹25,00,000",
    "₹25,00,000 - ₹50,00,000",
    "₹50,00,000+",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.location.trim()) newErrors.location = "Address is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);

    try {
      const SERVICE_ID = "service_a8t9sbt";
      const TEMPLATE_ID = "template_kkd9iri";
      const PUBLIC_KEY = "PinX8mgdGG_GoQx52";

      const templateParams = {
        to: "info@decodia.in",
        to_name: "Decodia",
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        project_type: formData.projectType,
        budget: formData.budget,
        company: formData.company || "Not provided",
        location: formData.location || "Not provided",
        message: formData.message || "No additional message provided",
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY,
      );
      
      setSubmitting(false);
      setSnackbarOpen(true);

      setFormData({
        name: "",
        email: "",
        phone: "",
        projectType: "Commercial Space Design",
        budget: "To be discussed",
        company: "",
        location: "",
        message: "",
      });
    } catch (error) {
      console.error("Error:", error);
      setSubmitting(false);

      const mailtoLink = `mailto:info@decodia.in?subject=Free Consultation Request: ${encodeURIComponent(
        formData.name,
      )}&body=Name: ${encodeURIComponent(formData.name)}%0D%0AEmail: ${encodeURIComponent(
        formData.email,
      )}%0D%0APhone: ${encodeURIComponent(
        formData.phone,
      )}%0D%0AProject Type: ${encodeURIComponent(
        formData.projectType,
      )}%0D%0ABudget: ${encodeURIComponent(
        formData.budget,
      )}%0D%0ACompany: ${encodeURIComponent(
        formData.company || "",
      )}%0D%0ALocation: ${encodeURIComponent(
        formData.location || "",
      )}%0D%0A%0D%0AAdditional Details:%0D%0A${encodeURIComponent(
        formData.message || "",
      )}`;

      if (
        window.confirm("Email service failed. Open your email client instead?")
      ) {
        window.location.href = mailtoLink;
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: { xs: 1, sm: 2 },
          width: "100%",
          maxWidth: "850px",
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 1000,
            color: colors.pureWhite,
            backgroundColor: alpha(colors.pureWhite, 0.1),
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha(colors.pureWhite, 0.2)}`,
            "&:hover": {
              backgroundColor: alpha(colors.pureWhite, 0.2),
              transform: "rotate(90deg)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <CloseIcon />
        </IconButton>

        <Zoom in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              background: colors.pureWhite,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "6px",
                background: `linear-gradient(90deg, ${colors.terracotta}, #FF8E53, ${colors.terracotta})`,
                backgroundSize: "300% 100%",
                animation: "gradientShift 3s ease infinite",
              },
              "@keyframes gradientShift": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
            }}
          >
            <Box sx={{ p: { xs: 3, sm: 4, md: 5 }, pt: 6 }}>
              {/* Header */}
              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Chip
                  label="FREE CONSULTATION"
                  sx={{
                    bgcolor: alpha(colors.terracotta, 0.1),
                    color: colors.terracotta,
                    mb: 2,
                    px: 3,
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    letterSpacing: "1px",
                    border: `1px solid ${alpha(colors.terracotta, 0.2)}`,
                    fontFamily: fonts.body,
                  }}
                  icon={
                    <SparklesIcon
                      sx={{ fontSize: 16, color: colors.terracotta }}
                    />
                  }
                />

                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: fonts.header,
                    fontWeight: 700,
                    mb: 2,
                    color: colors.charcoalGrey,
                    fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  }}
                >
                  Book Your Free Consultation
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: fonts.body,
                    color: colors.charcoalLight,
                    mb: 3,
                    fontSize: { xs: "0.95rem", md: "1rem" },
                  }}
                >
                  Fill in your details and we'll schedule your 30-minute
                  consultation
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2.5}>
                  {/* Row 1: Full Name and Email Address */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name **"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address **"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Row 2: Phone Number and Project Type */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number **"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      required
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Project Type *</InputLabel>
                      <Select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        label="Project Type *"
                      >
                        {projectTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Row 3: Estimated Budget and Company/Organization */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Estimated Budget</InputLabel>
                      <Select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        label="Estimated Budget"
                      >
                        {budgets.map((budget) => (
                          <MenuItem key={budget} value={budget}>
                            {budget}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company / Organization"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      placeholder="Optional"
                    />
                  </Grid>

                  {/* Row 4: Location - Full width */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      variant="outlined"
                      error={!!errors.location}
                      helperText={errors.location}
                      size="small"
                      placeholder="Optional - Enter your location"
                    />
                  </Grid>

                  {/* Row 5: Additional Details / Questions */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Details / ..."
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                      variant="outlined"
                      size="small"
                      placeholder="Optional - Tell us more about your project, requirements."
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={submitting}
                        sx={{
                          width: { xs: "100%", sm: "auto" },
                          minWidth: 250,
                          py: 1.5,
                          px: 4,
                          borderRadius: "12px",
                          background: `linear-gradient(135deg, ${colors.terracotta} 0%, ${colors.terracottaDark} 100%)`,
                          color: colors.pureWhite,
                          fontSize: "1rem",
                          fontWeight: 700,
                          fontFamily: fonts.body,
                          boxShadow: `0 8px 20px ${alpha(colors.terracotta, 0.3)}`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${colors.terracottaDark} 0%, ${colors.terracotta} 100%)`,
                            transform: "translateY(-2px)",
                            boxShadow: `0 12px 25px ${alpha(colors.terracotta, 0.5)}`,
                          },
                          "&:disabled": {
                            background: colors.charcoalLight,
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {submitting ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <CircularProgress size={20} color="inherit" />
                            <span>Submitting...</span>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span>SUBMIT REQUEST</span>
                            <ArrowOutwardIcon sx={{ fontSize: 18 }} />
                          </Box>
                        )}
                      </Button>

                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.charcoalLight,
                          fontFamily: fonts.body,
                          fontSize: "0.8rem",
                          textAlign: "center",
                        }}
                      >
                        <Verified
                          sx={{
                            fontSize: 12,
                            verticalAlign: "middle",
                            mr: 0.5,
                            color: "#295237",
                          }}
                        />
                        We'll contact you within 24 hours to schedule
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
        </Zoom>
      </DialogContent>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            borderRadius: "12px",
            bgcolor: "#295237",
            color: colors.pureWhite,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Request Submitted Successfully!
          </Typography>
          <Typography variant="caption">
            We'll contact you shortly to schedule your consultation.
          </Typography>
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ConsultationFormDialog;